// UK and US registration packs. Templates are assembled in the Console and
// hashed at Publish Live. The phone never sees a {{TOKEN}}.
//
// Required blocks become attestations (they may gate Submit). Optional blocks
// become consents (they must never gate Submit). Cookie stays a document.

import { CONSENT_PURPOSES } from "./events.js";

export const TERRITORIES = ["UK", "US"] as const;
export type Territory = (typeof TERRITORIES)[number];

export const PACK_PRESETS = ["enquiry", "prize", "game", "portrait", "physical"] as const;
export type PackPreset = (typeof PACK_PRESETS)[number];

export const SHARED_TOKENS = [
  "CLIENT_NAME",
  "CLIENT_LEGAL_ENTITY",
  "OPERATOR_NAME",
  "ACTIVATION_NAME",
  "TERMS_URL",
  "PRIVACY_URL",
  "COOKIE_URL",
  "RULES_URL",
  "RELEASE_URL",
  "MIN_AGE",
  "ELIGIBILITY_NOTE",
  "THIRD_PARTY_NAME",
  "RETENTION_MONTHS",
  "CONTACT_EMAIL",
  "ACTIVITY",
  "LEADERBOARD_WINNERS",
] as const;

export const US_ONLY_TOKENS = [
  "EXCLUDED_STATES",
  "SMS_TERMS_URL",
  "DO_NOT_SELL_URL",
  "SPONSOR_ADDRESS",
  "TCPA_TEXT",
] as const;

export const ALL_TOKENS = [...SHARED_TOKENS, ...US_ONLY_TOKENS] as const;
export type PackToken = (typeof ALL_TOKENS)[number];

export const DEFAULT_RETENTION_MONTHS = "12";
export const DEFAULT_MIN_AGE = "18";

export type BlockRole = "attestation" | "consent";
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

export type PackBlockDef = {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly role: BlockRole;
  readonly requirement: "always" | "conditional" | "optional";
  readonly template: string;
  readonly subtextTemplate?: string;
  readonly gameTemplate?: string;
  readonly gameSubtextTemplate?: string;
  readonly attestationKind?: "eligibility" | "terms_acceptance";
  readonly purpose?: ConsentPurpose;
  readonly urlTokens?: readonly PackToken[];
  readonly usOnly?: boolean;
  readonly ukOnly?: boolean;
  readonly needsTcpa?: boolean;
};

export const UK_BLOCKS: readonly PackBlockDef[] = [
  {
    id: "UK-01",
    number: "01",
    title: "Eligibility",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "eligibility",
    template: "I meet the eligibility requirements for {{ACTIVATION_NAME}}, including the minimum age of {{MIN_AGE}}.",
    subtextTemplate: "{{ELIGIBILITY_NOTE}}",
  },
  {
    id: "UK-02",
    number: "02",
    title: "Terms and conditions",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template: "I agree to the [{{ACTIVATION_NAME}} terms and conditions]({{TERMS_URL}}).",
    urlTokens: ["TERMS_URL"],
  },
  {
    id: "UK-03",
    number: "03",
    title: "Privacy",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template:
      "I have read the [privacy notice]({{PRIVACY_URL}}), which explains what {{CLIENT_NAME}} does with the details I enter here.",
    urlTokens: ["PRIVACY_URL"],
  },
  {
    id: "UK-04",
    number: "04",
    title: "How the details are handled",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template:
      "I understand that the details I enter here are collected for {{CLIENT_LEGAL_ENTITY}}, and are handled by its stand and technology supplier on its instructions, to run {{ACTIVATION_NAME}}, record my participation and answer what I asked for. {{CLIENT_NAME}} will keep them for up to {{RETENTION_MONTHS}} months.",
  },
  {
    id: "UK-05",
    number: "05",
    title: "Rules",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template: "I have read and agree to the [official rules]({{RULES_URL}}) for {{ACTIVATION_NAME}}.",
    subtextTemplate:
      "There is a free entry route. It is set out in the rules and it is no more expensive and no less convenient than any other route.",
    gameSubtextTemplate:
      "This is a game of skill. The top {{LEADERBOARD_WINNERS}} on the leaderboard win. How a score is judged is in the rules.",
    urlTokens: ["RULES_URL"],
  },
  {
    id: "UK-06",
    number: "06",
    title: "Public display",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template:
      "I agree that the name or tag I enter, and my score, may be shown on the leaderboard at this stand and on {{CLIENT_NAME}}'s channels for {{ACTIVATION_NAME}}.",
    gameTemplate:
      "I agree that the name or tag I enter, and my score, may be shown on the leaderboard at this stand and on {{CLIENT_NAME}}'s channels for {{ACTIVATION_NAME}}. The top {{LEADERBOARD_WINNERS}} on that leaderboard win.",
  },
  {
    id: "UK-07",
    number: "07",
    title: "Image capture and delivery",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template:
      "I agree that my photograph or video may be taken at this stand and sent to me at the email address or number I have given.",
  },
  {
    id: "UK-08",
    number: "08",
    title: "Sharing with a named third party",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template: "I agree that my details may also be given to {{THIRD_PARTY_NAME}} for {{ACTIVATION_NAME}}.",
  },
  {
    id: "UK-09",
    number: "09",
    title: "Email marketing",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_email",
    template:
      "Email me about {{CLIENT_NAME}} products, offers and events. Consent is not required to take part. I can unsubscribe at any time.",
  },
  {
    id: "UK-10",
    number: "10",
    title: "Text message marketing",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_sms",
    template:
      "Text me about {{CLIENT_NAME}} products, offers and events. Consent is not required to take part. I can stop these at any time by replying STOP.",
  },
  {
    id: "UK-11",
    number: "11",
    title: "Telephone contact",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_phone",
    template:
      "Call me about {{CLIENT_NAME}} products and services on the number I have given. Consent is not required to take part.",
  },
  {
    id: "UK-12",
    number: "12",
    title: "Follow-up on this enquiry only",
    role: "consent",
    requirement: "optional",
    purpose: "enquiry_follow_up",
    template:
      "Contact me about the specific thing I asked about at this stand. This is not marketing, it stops once my question is answered, and consent is not required to take part.",
  },
  {
    id: "UK-13",
    number: "13",
    title: "Image in marketing",
    role: "consent",
    requirement: "optional",
    purpose: "image_marketing",
    template:
      "{{CLIENT_NAME}} may use my photograph or video from this stand in its marketing, on the terms in the [photo and likeness release]({{RELEASE_URL}}). I can ask for this to stop by writing to {{CONTACT_EMAIL}}. Consent is not required to take part.",
    urlTokens: ["RELEASE_URL"],
  },
  {
    id: "UK-14",
    number: "14",
    title: "Profiling and matching",
    role: "consent",
    requirement: "optional",
    purpose: "profiling",
    template:
      "{{CLIENT_NAME}} may match my details with its other records to make what it sends me more relevant. Consent is not required to take part.",
  },
];

export const US_BLOCKS: readonly PackBlockDef[] = [
  {
    id: "US-01",
    number: "01",
    title: "Eligibility",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "eligibility",
    template:
      "I am a legal resident of the United States, I am {{MIN_AGE}} or older, and I meet the eligibility requirements for {{ACTIVATION_NAME}}.",
    subtextTemplate:
      "18 in most states, 19 in Alabama and Nebraska, 21 in Mississippi.{{EXCLUSION}} {{ELIGIBILITY_NOTE}}",
  },
  {
    id: "US-02",
    number: "02",
    title: "Terms and privacy",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template:
      "I agree to the [terms and conditions]({{TERMS_URL}}) and I have read the [privacy policy]({{PRIVACY_URL}}), including how my information is collected, used and shared for this experience.",
    urlTokens: ["TERMS_URL", "PRIVACY_URL"],
  },
  {
    id: "US-02-terms",
    number: "02",
    title: "Terms and conditions",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template: "I agree to the [{{ACTIVATION_NAME}} terms and conditions]({{TERMS_URL}}).",
    urlTokens: ["TERMS_URL"],
  },
  {
    id: "US-03",
    number: "03",
    title: "Privacy",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template:
      "I have read the [privacy policy]({{PRIVACY_URL}}), which explains what {{CLIENT_NAME}} does with the information I enter here.",
    urlTokens: ["PRIVACY_URL"],
  },
  {
    id: "US-04",
    number: "04",
    title: "How the information is handled",
    role: "attestation",
    requirement: "always",
    attestationKind: "terms_acceptance",
    template:
      "I understand that the information I provide may be shared with {{CLIENT_LEGAL_ENTITY}}, {{OPERATOR_NAME}} and their service providers to run {{ACTIVATION_NAME}}, record or verify my participation, and provide what I asked for, as described in the privacy policy.",
  },
  {
    id: "US-05",
    number: "05",
    title: "Official rules",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template: "I have read and agree to the [official rules]({{RULES_URL}}) for {{ACTIVATION_NAME}}.",
    subtextTemplate:
      "No purchase necessary. A purchase does not improve your chance of winning. See the official rules for the free method of entry.",
    gameSubtextTemplate:
      "This is a game of skill, not a game of chance. The top {{LEADERBOARD_WINNERS}} on the leaderboard win. See the official rules.",
    urlTokens: ["RULES_URL"],
  },
  {
    id: "US-06",
    number: "06",
    title: "Public display",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template:
      "I agree that the name or tag I enter, and my score, may be shown on the leaderboard at this stand and on {{CLIENT_NAME}}'s channels for {{ACTIVATION_NAME}}.",
    gameTemplate:
      "I agree that the name or tag I enter, and my score, may be shown on the leaderboard at this stand and on {{CLIENT_NAME}}'s channels for {{ACTIVATION_NAME}}. The top {{LEADERBOARD_WINNERS}} on that leaderboard win.",
  },
  {
    id: "US-07",
    number: "07",
    title: "Photo and video capture",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template:
      "I agree that my photograph or video may be taken at this stand and sent to me at the email address or number I provide.",
  },
  {
    id: "US-08",
    number: "08",
    title: "Sharing with a named third party",
    role: "attestation",
    requirement: "conditional",
    attestationKind: "terms_acceptance",
    template: "I agree that my information may also be provided to {{THIRD_PARTY_NAME}} for {{ACTIVATION_NAME}}.",
  },
  {
    id: "US-09",
    number: "09",
    title: "Email marketing",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_email",
    template:
      "Yes, send me emails from {{CLIENT_NAME}} about products, services, offers and events. Consent is not required to take part or to make a purchase, and I can unsubscribe at any time.",
  },
  {
    id: "US-10",
    number: "10",
    title: "Text message marketing",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_sms",
    needsTcpa: true,
    template:
      "Yes, text me from {{CLIENT_NAME}} about products, services, offers and events. Consent is not required to take part or to make a purchase. I can stop these at any time by replying STOP.",
    urlTokens: ["SMS_TERMS_URL"],
  },
  {
    id: "US-11",
    number: "11",
    title: "Telephone contact",
    role: "consent",
    requirement: "optional",
    purpose: "marketing_phone",
    needsTcpa: true,
    template:
      "Yes, call me from {{CLIENT_NAME}} about products and services on the number I have given. Consent is not required to take part or to make a purchase.",
  },
  {
    id: "US-12",
    number: "12",
    title: "Follow-up on this request only",
    role: "consent",
    requirement: "optional",
    purpose: "enquiry_follow_up",
    template:
      "Contact me about the specific product or service I asked about at this stand. Consent is not required to take part.",
  },
  {
    id: "US-13",
    number: "13",
    title: "Photo and likeness in marketing",
    role: "consent",
    requirement: "optional",
    purpose: "image_marketing",
    template:
      "{{CLIENT_NAME}} may use my photograph, video and likeness from this stand in its marketing, on the terms of the [photo and likeness release]({{RELEASE_URL}}). Consent is not required to take part.",
    urlTokens: ["RELEASE_URL"],
  },
  {
    id: "US-14",
    number: "14",
    title: "Data matching",
    role: "consent",
    requirement: "optional",
    purpose: "profiling",
    template:
      "{{CLIENT_NAME}} may combine the information I provide with its other records to make what it sends me more relevant. Consent is not required to take part.",
  },
];

export const OPTIONAL_DIVIDER = {
  UK: "The boxes below are optional. You can take part without ticking them.",
  US: "The boxes below are optional. You can take part without checking them.",
} as const;

export const NOTICE_TEXT = {
  UK: "{{CLIENT_LEGAL_ENTITY}} decides how the details you enter here are used. The stand and its technology are run by {{OPERATOR_NAME}} on its behalf. Your details are held in the UK or the EU for up to {{RETENTION_MONTHS}} months. To see, correct or delete what is held, or to withdraw any permission you gave here, write to {{CONTACT_EMAIL}}. Full detail is in the privacy notice.",
  US: "{{CLIENT_LEGAL_ENTITY}} is responsible for how the information you provide here is used. The stand and its technology are operated by {{OPERATOR_NAME}} on its behalf. Information is kept for up to {{RETENTION_MONTHS}} months. To access, correct or delete it, contact {{CONTACT_EMAIL}} or write to {{SPONSOR_ADDRESS}}. See the privacy policy for full detail.{{DO_NOT_SELL}}",
} as const;

export const CUSTOM_ACK_TEMPLATES = [
  "I have obtained any permission needed to take part in {{ACTIVITY}}.",
  "I understand the risks of {{ACTIVITY}} and choose to take part.",
] as const;

export const MAXIMUM_CUSTOM_ACKS = 2;

export const PRESET_COPY: Readonly<Record<PackPreset, { readonly title: string; readonly hint: string }>> = {
  enquiry: { title: "Enquiry", hint: "Qualified enquiry. No prize, no camera." },
  prize: { title: "Prize draw / Sweepstakes", hint: "A prize. Eligibility and rules come on." },
  game: {
    title: "Game / Skill-based giveaway",
    hint: "The top places on the leaderboard win. Eligibility and rules come on.",
  },
  portrait: { title: "Portrait and Mirror", hint: "A photograph taken and sent." },
  physical: { title: "Physical activity", hint: "A physical activity and a custom acknowledgement." },
};

export const PRESET_BLOCKS: Readonly<
  Record<PackPreset, { readonly required: readonly string[]; readonly optional: readonly string[] }>
> = {
  enquiry: { required: ["02", "04"], optional: ["12", "09"] },
  prize: { required: ["01", "02", "04", "05"], optional: ["09", "10", "11"] },
  game: { required: ["01", "02", "04", "05", "06"], optional: ["09", "12"] },
  portrait: { required: ["02", "04", "07"], optional: ["13", "09"] },
  physical: { required: ["01", "02", "04"], optional: ["09", "12"] },
};

export function blocksFor(territory: Territory): readonly PackBlockDef[] {
  return territory === "UK" ? UK_BLOCKS : US_BLOCKS;
}

export function tokensFor(territory: Territory): readonly PackToken[] {
  return territory === "UK" ? SHARED_TOKENS : ALL_TOKENS;
}

export function templatesFor(
  block: PackBlockDef,
  preset: PackPreset | null,
): { readonly template: string; readonly subtextTemplate?: string } {
  if (preset === "game") {
    return {
      template: block.gameTemplate ?? block.template,
      ...(block.gameSubtextTemplate !== undefined || block.subtextTemplate !== undefined
        ? { subtextTemplate: block.gameSubtextTemplate ?? block.subtextTemplate }
        : {}),
    };
  }
  return {
    template: block.template,
    ...(block.subtextTemplate !== undefined ? { subtextTemplate: block.subtextTemplate } : {}),
  };
}

export function defaultTokens(): Record<PackToken, string> {
  const tokens = {} as Record<PackToken, string>;
  for (const name of ALL_TOKENS) tokens[name] = "";
  tokens.RETENTION_MONTHS = DEFAULT_RETENTION_MONTHS;
  tokens.MIN_AGE = DEFAULT_MIN_AGE;
  return tokens;
}

const TOKEN = /\{\{([A-Z_]+)\}\}/g;

/**
 * Known document links in pack copy. A tick that was rewritten still carries
 * these phrases; the URL in the markdown is the hole Names and URLs fills, and
 * it has to move when that field moves.
 */
const PACK_URL_LINKS: readonly { readonly phrase: string; readonly token: PackToken }[] = [
  { phrase: String.raw`\[(official rules)\]\((https?:[^)\s]+)\)`, token: "RULES_URL" },
  { phrase: String.raw`\[((?:[^\]]* )?terms and conditions)\]\((https?:[^)\s]+)\)`, token: "TERMS_URL" },
  { phrase: String.raw`\[(privacy policy)\]\((https?:[^)\s]+)\)`, token: "PRIVACY_URL" },
  { phrase: String.raw`\[(privacy notice)\]\((https?:[^)\s]+)\)`, token: "PRIVACY_URL" },
  { phrase: String.raw`\[(photo and likeness release)\]\((https?:[^)\s]+)\)`, token: "RELEASE_URL" },
  { phrase: String.raw`\[(SMS terms)\]\((https?:[^)\s]+)\)`, token: "SMS_TERMS_URL" },
];

/**
 * Put the current URL tokens into markdown links that still name those
 * documents. A rewritten sentence is kept; only the href inside `[official
 * rules](…)` (and the other pack phrases) is brought up to date.
 */
export function syncPackUrlsInText(text: string, tokens: Readonly<Record<string, string>>): string {
  let next = text;
  for (const { phrase, token } of PACK_URL_LINKS) {
    const url = (tokens[token] ?? "").trim();
    if (url === "") continue;
    next = next.replace(new RegExp(phrase, "gi"), (_all, label: string) => `[${label}](${url})`);
  }
  return next;
}

export function assemble(template: string, tokens: Readonly<Record<string, string>>): string {
  return fillTemplate(template, tokens, "publish");
}

/**
 * The same sentences a visitor will see, with unfilled holes named rather than
 * collapsed into a blank. View Live is a draft preview: "for ," reads as a
 * broken page, and "for ‹activation name›" is the hole somebody still has to
 * type. Publish still uses assemble, which leaves a hole empty so a missing
 * token stays a missing token in the signed wording.
 */
export function assembleForPreview(template: string, tokens: Readonly<Record<string, string>>): string {
  return fillTemplate(template, tokens, "preview");
}

function fillTemplate(
  template: string,
  tokens: Readonly<Record<string, string>>,
  mode: "publish" | "preview",
): string {
  const exclusion = (tokens.EXCLUDED_STATES ?? "").trim();
  const doNotSell = (tokens.DO_NOT_SELL_URL ?? "").trim();
  const hole = (name: string): string => {
    const label = name === "CLIENT_LEGAL_ENTITY" ? "client legal name" : name.replaceAll("_", " ").toLowerCase();
    return mode === "preview" ? `‹${label}›` : "";
  };
  const withSpecials = template
    .replaceAll("{{EXCLUSION}}", exclusion === "" ? hole("EXCLUDED_STATES") : ` Not open to residents of ${exclusion}.`)
    .replaceAll(
      "{{DO_NOT_SELL}}",
      doNotSell === ""
        ? hole("DO_NOT_SELL_URL")
        : ` [Do not sell or share my personal information](${doNotSell})`,
    );
  return withSpecials
    .replace(TOKEN, (_, name: string) => {
      const value = (tokens[name] ?? "").trim();
      return value === "" ? hole(name) : value;
    })
    .replace(/  +/g, " ")
    .replace(/ \./g, ".")
    .trim();
}

export function missingTokens(template: string, tokens: Readonly<Record<string, string>>): readonly string[] {
  const missing: string[] = [];
  for (const match of template.matchAll(TOKEN)) {
    const name = match[1];
    if (name === undefined) continue;
    if (name === "EXCLUSION" || name === "DO_NOT_SELL" || name === "ELIGIBILITY_NOTE") continue;
    if ((tokens[name] ?? "").trim() === "") missing.push(name);
  }
  return [...new Set(missing)];
}
